import { t } from "@lingui/macro";
import { useRoute, RouteProp } from "@react-navigation/native";
import graphql from "babel-plugin-relay/macro";
import { useAtomValue } from "jotai";
import { groupBy } from "lodash";
import React, { useState } from "react";
import { PreloadedQuery, usePreloadedQuery } from "react-relay";

import { DiningBookingWizard } from "@/features/BookingWizard/DiningBookingWizard/DiningBookingWizard";
import { PrivateTimeSlotCard } from "@whoosh/design-system/src/apps/PatronApp/screens/teesheet/components/TeeSheet/TimeSlotCard/";
import {
  Box,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";
import { formatHHMM } from "@whoosh/utilities/src/dates";

import { currentProfileAtom } from "../../../../features/Club/atoms";
import { selectedFacilityId_atom, selectedPartySize_atom } from "../atoms";
import { useCreateReservationHold } from "../hooks/useCreateReservationHold";
import { useUpdateDiningBooking } from "../hooks/useUpdateDiningBooking";
import { ExistingDiningBooking } from "./DiningAvailability";
import { DiningAvailabilityListQuery } from "./__generated__/DiningAvailabilityListQuery.graphql";

type RootStackParamList = {
  DiningAvailabilityRoute: {
    date: string;
    facilitySlug: string;
  };
};

type DiningAvailabilityScreenRouteProp = RouteProp<
  RootStackParamList,
  "DiningAvailabilityRoute"
>;

type Props = {
  existingBooking?: ExistingDiningBooking;
  queryRef: PreloadedQuery<DiningAvailabilityListQuery>;
};
export const DiningAvailabilityList = ({
  existingBooking,
  queryRef,
}: Props) => {
  const route = useRoute<DiningAvailabilityScreenRouteProp>();
  const date = route.params?.date;
  const data = usePreloadedQuery(DiningAvailabilityListQueryString, queryRef);
  const [createDiningBookingHold] = useCreateReservationHold();
  const partySize = useAtomValue(selectedPartySize_atom);
  const facilityId = useAtomValue(selectedFacilityId_atom);
  const currentProfile = useAtomValue(currentProfileAtom);
  const [selected, setSelected] = useState<number | null>(null);
  const [updateDiningBooking] = useUpdateDiningBooking();

  const isCurrent = (slot: any) => {
    return existingBooking
      ? slot.time === existingBooking.time &&
          date === existingBooking.date &&
          slot.roomName === existingBooking.roomName
      : false;
  };

  const diningSlots = data.node?.diningSlots?.map((ds, i) => ({
    key: i,
    ...ds,
    fTime: formatHHMM(ds?.time || ""),
    current: isCurrent(ds),
  }));
  const groupedSlots = Object.values(groupBy(diningSlots, "fTime"));

  const hasDiningSlots = !!diningSlots?.length;

  const [currentReservationHoldId, setCurrentReservationHoldId] = useState<
    string | null
  >(null);

  const handlePress = (slot: any) => {
    //All of the success logic lives in this callback, so we can just pivot here
    // on new/old flow
    const reservationCreationCallback = (reservationHoldId: string | null) => {
      setSelected(null);
      if (!reservationHoldId) {
        return;
      }

      if (existingBooking) {
        const ownerId =
          existingBooking.ownerId ?? currentProfile?.memberId ?? "";

        if (!ownerId) {
          return;
        }

        updateDiningBooking({
          bookingId: existingBooking.id,
          ownerId,
          reservationHoldId,
          notes: existingBooking.notesFromMember ?? "",
          dietaryTags: existingBooking.dietaryTags ?? undefined,
          occasionTags: existingBooking.occasionTags ?? undefined,
          otherPatronIds: existingBooking.otherPatronIds ?? undefined,
        });

        return;
      }

      setCurrentReservationHoldId(reservationHoldId);
    };

    // this prevents them from clicking multiple time slots while we are loading
    setSelected(slot.key);
    if (!selected) {
      createDiningBookingHold(
        {
          shiftPersistentId: slot.shiftPersistentId,
          accessPersistentId: slot.accessPersistentId,
          roomName: slot.roomName,
          ownerId: currentProfile?.memberId || "",
          time: slot.time,
          facilityId: facilityId || data.node?.id || "",
          date,
          partySize,
          policy: slot.policy,
        },
        reservationCreationCallback
      );
    }
  };
  return hasDiningSlots ? (
    <VStack>
      {groupedSlots.map((group, i) =>
        groupedSlots.length > 1 ? (
          <VStack key={`group-${i}`} my={2} gap={1}>
            {group.map((slot, j) => (
              <PrivateTimeSlotCard
                key={`${slot.time}-${slot.roomName}`}
                time={slot.fTime || ""}
                onPress={() => handlePress(slot)}
                players={[]}
                canUpdate={true}
                ctaText={selected === slot.key ? t`...Loading` : t`Reserve`}
                location={slot.roomName}
                borderRadius={{
                  top: j === 0 ? "2" : "0",
                  bottom: j === group.length - 1 ? "2" : "0",
                }}
              />
            ))}
          </VStack>
        ) : (
          <Box my={2}>
            <PrivateTimeSlotCard
              key={`group-${i}`}
              time={group[0].fTime || ""}
              onPress={() => handlePress(group[0])}
              players={[]}
              canUpdate={true}
              ctaText={selected === group[0].key ? t`...Loading` : t`Reserve`}
              location={group[0].roomName}
              borderRadius={{
                top: "2",
                bottom: "2",
              }}
            />
          </Box>
        )
      )}
      <DiningBookingWizard
        isOpen={!!currentReservationHoldId}
        onClose={() => {
          setCurrentReservationHoldId(null);
        }}
        reservationHoldId={currentReservationHoldId}
        membershipTypeId={
          data.session?.currentClientProfile?.member?.membershipType?.id || null
        }
        currentPatronId={data.session?.currentClientProfile?.member?.id || null}
        clubSlug={data.session?.currentClientProfile?.member?.club?.slug || ""}
      />
    </VStack>
  ) : (
    <Text textAlign="center">{t`No availability, please try another search.`}</Text>
  );
};

export const DiningAvailabilityListQueryString = graphql`
  query DiningAvailabilityListQuery(
    $facilityId: ID!
    $startTime: Time!
    $endTime: Time!
    $date: Date!
    $partySize: Int!
    $reservationId: String
  ) {
    session {
      currentClientProfile {
        member {
          ... on Member {
            id
          }
          ... on Customer {
            id
          }
          membershipType {
            id
          }
          club {
            slug
          }
        }
      }
    }
    node(id: $facilityId) {
      ... on Facility {
        id
        diningSlots(
          date: $date
          startTime: $startTime
          endTime: $endTime
          partySize: $partySize
          reservationId: $reservationId
        ) {
          time
          shiftPersistentId
          accessPersistentId
          roomName
          policy
        }
      }
    }
  }
`;
